import { getExerciseById } from '@/data/exercises';
import { getTrainingExperienceSettings } from '@/data/training-environment';
import { estimateTrainingMinutes } from '@/data/personalized-training';
import { normalizeLibraryCustomPlan } from '@/lib/libraryReferenceMapping';
import type { ICustomPlan } from '@/hooks/useCustomPlans';
import type { ExerciseConfiguration, MemberPlanDay, MemberTrainingPlan } from '@/lib/memberTrainingDay';
import { copyScheduleDay } from './scheduleDates';

export function dayExerciseSettings(plan: MemberTrainingPlan, day: MemberPlanDay): Record<string, ExerciseConfiguration> {
  const experience = getTrainingExperienceSettings(plan.trainingMonths);
  const entries = [...(day.warmupIds || []), ...(day.exerciseIds || []), ...(day.stretchIds || [])];
  return Object.fromEntries(entries.map(id => {
    const ex = getExerciseById(id);
    return [id, { ...(day.exerciseSettings?.[id] || {
      sets: day.scheduleSource === 'custom' || ex?.isWarmup || ex?.isStretch ? ex?.sets || 1 : experience.sets,
      reps: day.scheduleSource === 'custom' || /秒|分钟/.test(ex?.reps || '') ? ex?.reps || '10' : experience.reps,
      restSeconds: day.scheduleSource === 'custom' || ex?.isWarmup || ex?.isStretch ? ex?.restSeconds ?? 60 : experience.restSec,
    }) }];
  }));
}
export function estimateScheduleDuration(ids: string[], settings: Record<string, ExerciseConfiguration>): number {
  return Math.max(1, estimateTrainingMinutes({ dayOfWeek: 1, type: 'strength', label: '', subtitle: '',
    durationMin: 0, exerciseIds: ids, warmupIds: [], stretchIds: [] }, settings));
}
export function customPlanAsScheduledDay(custom: ICustomPlan, dayOfWeek: number): MemberPlanDay {
  const mapped = normalizeLibraryCustomPlan(custom);
  if (!mapped.exerciseIds.length || mapped.exerciseIds.some(id => !getExerciseById(id))) throw new Error('组合中有不可用的动作，请先编辑组合');
  const settings = Object.fromEntries(mapped.exerciseIds.map(id => {
    const ex = getExerciseById(id)!;
    return [id, { ...(mapped.exerciseConfigs?.[id] || { sets: ex.sets, reps: ex.reps, restSeconds: ex.restSeconds }) }];
  }));
  return { dayOfWeek, type: 'strength', label: mapped.name, subtitle: '自定义组合', scheduleSource: 'custom',
    customPlanId: mapped.id, exerciseIds: [...mapped.exerciseIds], exerciseSettings: settings,
    warmupIds: [], stretchIds: [], durationMin: estimateScheduleDuration(mapped.exerciseIds, settings) };
}
export function recommendedScheduleDays(plan: MemberTrainingPlan): MemberPlanDay[] {
  return (plan.recommendedWeekDays || plan.weekDays).filter(day => day.type === 'strength' && day.scheduleSource !== 'custom').map(copyScheduleDay);
}
export function moveScheduledExercise(day: MemberPlanDay, index: number, delta: number): MemberPlanDay {
  const ids = [...(day.exerciseIds || [])];
  if (index + delta < 0 || index + delta >= ids.length) return day;
  [ids[index], ids[index + delta]] = [ids[index + delta], ids[index]];
  return { ...day, exerciseIds: ids };
}
export function validateScheduleDays(days: MemberPlanDay[]): void {
  if (days.length !== 7 || new Set(days.map(d => d.dayOfWeek)).size !== 7
    || days.some(d => !Number.isInteger(d.dayOfWeek) || d.dayOfWeek < 1 || d.dayOfWeek > 7)) throw new Error('请完整安排周一到周日');
  for (const day of days) {
    if (!['strength', 'cardio', 'hiit', 'active-rest', 'rest'].includes(day.type)) throw new Error('请选择有效的训练类型');
    if (!Number.isFinite(day.durationMin) || day.durationMin < 0 || day.durationMin > 600) throw new Error('训练时长需在0–600分钟内');
    if (day.type === 'cardio' && (!day.cardioItem?.trim() || day.durationMin < 1)) throw new Error('请填写有氧方式和时长');
    if (day.type !== 'strength') continue;
    if (!day.exerciseIds?.length || day.exerciseIds.some(id => !getExerciseById(id))) throw new Error(`${day.label}还没有有效的训练动作`);
    for (const c of Object.values(day.exerciseSettings || {})) {
      if (!Number.isInteger(c.sets) || c.sets < 1 || c.sets > 30 || !c.reps.trim() || !/[1-9]/.test(c.reps)
        || c.reps.length > 30 || !Number.isInteger(c.restSeconds) || c.restSeconds < 0 || c.restSeconds > 600) throw new Error('请检查组数、次数和休息时间');
    }
  }
}
